import { Client } from "@xmpp/client"
import { Element } from "@xmpp/xml"
import { parseArchivedTimestamp, parseStanza } from "../../lib/stanzaParser"
import MessageParser from "../../lib/messageParser"
import MarkerParser from "../../lib/markerParser"
import {
  ArchivedBatchPayload,
  DeliveryOrder,
  DocumentDriverContext,
  DocumentLink,
  ExtractedMetadata,
  Fasten,
  Marker,
  Message,
  MessageFileShared,
  MessageRetraction,
  Reaction,
} from "../../reducers/messagesSlice"
import { Order } from "./MAM"
import ReactionsParser from "../../lib/reactionsParser"
import MessageFileSharedParser from "../../lib/messageFileSharedParser"
import RetractionParser from "../../lib/retractionParser"
import FastenParser from "../../lib/fastenParser"
import DeliveryOrderParser from "@/lib/deliveryOrderParser"
import ExtractedMetadataParser from "@/lib/extractedMetadataParser"
import DocumentDriverContextParser from "@/lib/documentDriverContextParser"
import DocumentLinkParser from "@/lib/documentLinkParser"

export type ProcessResults = {
  messagesCount: number
  markersCount: number
  reactionsCount: number
  messageFileSharedCount: number
  messageRetractionsCounts: number
  extractedMetadataCount: number
  documentDriverContextCount: number
  deliveryOrdersCount: number
  documentLinksCount: number
  fastensCount: number
}

export type GroupedMessages = {
  corrections: Message[]
  messages: Message[]
}

export type AccumulatedData = {
  markersAccumulator: Marker[][]
  messagesAccumulator: Message[][]
  reactionsAccumulator: Reaction[][]
  messageFileSharesAccumulator: MessageFileShared[][]
  messageRetractionsAccumulator: MessageRetraction[][]
  extractedMetadataAccumulator: ExtractedMetadata[][]
  documentDriverContextAccumulator: DocumentDriverContext[][]
  deliveryOrdersAccumulator: DeliveryOrder[][]
  documentLinksAccumulator: DocumentLink[][]
  routeUpdatesAccumulator: Fasten[][]
}

class MAMStanzaProcessor {
  static groupByMessagesAndCorrections(messages: Message[]): GroupedMessages {
    return messages.reduce(
      (acc, message) => {
        if (message.replaceId) {
          acc.corrections.push(message)
        } else {
          acc.messages.push(message)
        }

        return acc
      },
      { corrections: [], messages: [] } as GroupedMessages,
    )
  }

  static normalizePagesAscending<
    T extends
      | Message
      | Marker
      | Reaction
      | MessageFileShared
      | MessageRetraction
      | ExtractedMetadata
      | DocumentDriverContext
      | DeliveryOrder
      | DocumentLink
      | Fasten
      | string,
  >(pages: T[][], archiveOrder: Order, flippedPages: boolean): T[] {
    if (archiveOrder === "asc" && !flippedPages) {
      return pages.flat()
    }

    if (archiveOrder === "asc" && flippedPages) {
      return pages.flatMap((page) => page.reverse())
    }

    if (archiveOrder === "desc" && !flippedPages) {
      return pages.reverse().flat()
    }

    if (archiveOrder === "desc" && flippedPages) {
      return pages.flat().reverse()
    }

    throw new Error("Invalid archive order")
  }

  /**
   * Process accumulated data and prepare a payload for batch processing
   * @param data Accumulated data from MAM stanza processor
   * @param order Archive order ("asc" or "desc")
   * @param flipPage Whether to flip the page
   * @param myJid User's JID
   * @param mode Batch mode ("prepend" or "append")
   * @returns Payload ready for processArchivedBatch action
   */
  static processAccumulatedData(
    data: AccumulatedData,
    order: Order,
    flipPage: boolean,
    myJid: string,
    mode: "prepend" | "append",
  ): ArchivedBatchPayload {
    const {
      messagesAccumulator,
      markersAccumulator,
      reactionsAccumulator,
      messageFileSharesAccumulator,
      messageRetractionsAccumulator,
      extractedMetadataAccumulator,
      documentDriverContextAccumulator,
      deliveryOrdersAccumulator,
      documentLinksAccumulator,
      routeUpdatesAccumulator,
    } = data

    // Normalize all data with proper order
    const retractions = this.normalizePagesAscending(
      messageRetractionsAccumulator,
      order,
      flipPage,
    )

    const allMessages = this.normalizePagesAscending(
      messagesAccumulator,
      order,
      flipPage,
    ) as Message[]

    const groupedMessages = this.groupByMessagesAndCorrections(allMessages)

    const reactions = this.normalizePagesAscending(
      reactionsAccumulator,
      order,
      flipPage,
    )

    const fileShares = this.normalizePagesAscending(
      messageFileSharesAccumulator,
      order,
      flipPage,
    )

    const extractedMetadata = this.normalizePagesAscending(
      extractedMetadataAccumulator,
      order,
      flipPage,
    )

    const documentDriverContext = this.normalizePagesAscending(
      documentDriverContextAccumulator,
      order,
      flipPage,
    )

    const deliveryOrders = this.normalizePagesAscending(
      deliveryOrdersAccumulator,
      order,
      flipPage,
    )

    const documentLinks = this.normalizePagesAscending(
      documentLinksAccumulator,
      order,
      flipPage,
    )

    const fastens = this.normalizePagesAscending(
      routeUpdatesAccumulator,
      order,
      flipPage,
    )

    const markers = this.normalizePagesAscending(
      markersAccumulator,
      order,
      flipPage,
    )

    return {
      messages: groupedMessages.messages,
      corrections: groupedMessages.corrections,
      reactions,
      retractions,
      fileShares,
      extractedMetadata,
      documentDriverContext,
      deliveryOrders,
      documentLinks,
      fastens,
      markers,
      myJid,
      mode,
    }
  }

  static accumulatePages(
    messagesAccumulator: Message[][],
    markersAccumulator: Marker[][],
    reactionsAccumulator: Reaction[][],
    messageFileSharesAccumulator: MessageFileShared[][],
    messageRetractionsAccumulator: MessageRetraction[][],
    extractedMetadataAccumulator: ExtractedMetadata[][],
    documentDriverContextAccumulator: DocumentDriverContext[][],
    deliveryOrdersAccumulator: DeliveryOrder[][],
    documentLinksAccumulator: DocumentLink[][],
    fastensAccumulator: Fasten[][],
    client: Client,
  ) {
    return (stanzas: Element[]): ProcessResults => {
      const messages: Message[] = []
      const markers: Marker[] = []
      const reactions: Reaction[] = []
      const messageFileShares: MessageFileShared[] = []
      const messageRetractions: MessageRetraction[] = []
      const extractedMetadata: ExtractedMetadata[] = []
      const documentDriverContext: DocumentDriverContext[] = []
      const deliveryOrders: DeliveryOrder[] = []
      const documentLinks: DocumentLink[] = []
      const fastens: Fasten[] = []

      stanzas.forEach((stanza) => {
        const result = parseStanza(stanza, client.jid?.bare().toString())

        if (!result) {
          return
        }

        const { stanza: messageStanza, type } = result

        if (type === "archived_message") {
          const message = MessageParser.parse(messageStanza, {
            timestamp: parseArchivedTimestamp(stanza),
          })

          messages.push(message)

          // Also check for delivery order data
          const deliveryOrder = DeliveryOrderParser.parse(
            messageStanza,
            parseArchivedTimestamp(stanza),
          )
          if (deliveryOrder) {
            deliveryOrders.push(deliveryOrder)
          }

          return
        }

        if (type === "ai_widget_map") {
          // MAM replay of an ai-widget-map stanza: keep the parsed map data
          // on the Message so the "Show map" button stays available after
          // reload. We don't push to the aiWidgetMap slice here — that slice
          // is transient by design and only fills on click.
          const message = MessageParser.parse(messageStanza, {
            timestamp: parseArchivedTimestamp(stanza),
          })
          message.aiWidgetMap = result.parsed
          messages.push(message)
          return
        }

        if (type === "archived_chat_marker") {
          const marker = MarkerParser.parse(messageStanza, {
            timestamp: parseArchivedTimestamp(stanza),
          })

          markers.push(marker)
          return
        }

        if (type === "archived_reaction") {
          const reaction = ReactionsParser.parse(
            messageStanza,
            parseArchivedTimestamp(stanza),
          )
          if (!reaction) return

          reactions.push(reaction)
          return
        }

        if (type === "archived_message_file_shared") {
          const messageFileShared = MessageFileSharedParser.parse(
            messageStanza,
            parseArchivedTimestamp(stanza),
          )

          if (!messageFileShared) return

          messageFileShares.push(messageFileShared)
        }

        if (type === "archived_retraction") {
          const messageRetraction = RetractionParser.parse(
            messageStanza,
            parseArchivedTimestamp(stanza),
          )

          if (!messageRetraction) return

          messageRetractions.push(messageRetraction)
        }

        if (type === "archived_document_driver_context") {
          const driverContext = DocumentDriverContextParser.parse(
            messageStanza,
            parseArchivedTimestamp(stanza),
          )

          if (!driverContext) return

          documentDriverContext.push(driverContext)
        }

        if (type === "archived_extracted_metadata") {
          const metadata = ExtractedMetadataParser.parse(
            messageStanza,
            parseArchivedTimestamp(stanza),
          )

          if (!metadata) return

          extractedMetadata.push(metadata)
        }

        if (type === "archived_document_link") {
          const documentLink = DocumentLinkParser.parse(
            messageStanza,
            parseArchivedTimestamp(stanza),
          )

          if (!documentLink) return

          documentLinks.push(documentLink)
        }

        if (type === "archived_fasten") {
          const fasten = FastenParser.parse(
            messageStanza,
            parseArchivedTimestamp(stanza),
          )

          if (!fasten) return

          fastens.push(fasten)
        }
      })

      messagesAccumulator.push(messages)
      markersAccumulator.push(markers)
      reactionsAccumulator.push(reactions)
      messageFileSharesAccumulator.push(messageFileShares)
      messageRetractionsAccumulator.push(messageRetractions)
      documentDriverContextAccumulator.push(documentDriverContext)
      extractedMetadataAccumulator.push(extractedMetadata)
      deliveryOrdersAccumulator.push(deliveryOrders)
      documentLinksAccumulator.push(documentLinks)
      fastensAccumulator.push(fastens)

      return {
        messagesCount: messages.length,
        markersCount: markers.length,
        reactionsCount: reactions.length,
        messageFileSharedCount: messageFileShares.length,
        messageRetractionsCounts: messageRetractions.length,
        extractedMetadataCount: extractedMetadata.length,
        documentDriverContextCount: documentDriverContext.length,
        deliveryOrdersCount: deliveryOrders.length,
        documentLinksCount: documentLinks.length,
        fastensCount: fastens.length,
      }
    }
  }
}

export default MAMStanzaProcessor
